import { ChangeEvent, JSX, RefObject, useRef, useState } from 'react';
import {
    Alert,
    Box,
    Button,
    Chip,
    CircularProgress,
    Divider,
    Stack,
    TextField,
    Typography,
} from '@mui/material';
import SendIcon from '@mui/icons-material/Send';
import { useGetMany, useNotify, useRefresh } from 'react-admin';
import { RichTextEditor, RichTextEditorRef } from 'mui-tiptap';
import useExtensions from '../hooks/extension-hooks';
import EditorControls from './EditorControls';
import { AdminMiniDialog } from './AdminMiniDialog';
import { AdminMiniScoreCard } from './AdminMiniScoreCard';
import { AdminMiniEmailFrame } from './AdminMiniEmailFrame';
import { AdminMiniDescriptor, AdminMiniSendReport } from '../admin-mini.types';
import { AdminMiniAccess } from '../hooks/admin-mini-access';
import { useAdminMiniSendEmail } from '../hooks/admin-mini-requests';

export interface AdminMiniResultModalProps {
    tool: AdminMiniDescriptor;
    open: boolean;
    onClose: () => void;
    /** The result record, as react-admin loaded it. */
    result?: any;
    /** The run it belongs to, needed to summarise it. */
    runId?: number | string;
    access: AdminMiniAccess;
}

/**
 * Whichever collection this tool keeps its messages in. The two entities differ:
 * `ComplianceSnifferResult` calls it `emailMessages` and `MysteryShopperResult` calls it `emails`.
 */
function messageRefsOf(result: any): any[] {
    const refs: any = result?.emailMessages ?? result?.emails ?? [];

    return Array.isArray(refs) ? refs : [];
}

/**
 * The ids to fetch, when the record carries references rather than records.
 *
 * API Platform serialises an unexpanded relation as an array of IRIs, and react-admin hands the
 * record over as it arrives. Rendering those directly is what produced thirteen rows of
 * "(no subject)" marked "Sent": every field read off a string is `undefined`, including
 * `direction`, so every message looked outbound. Found in a browser; jsdom would have drawn the
 * same thirteen rows without complaint.
 */
function idsNeeding(refs: any[]): string[] {
    return refs
        .filter((ref: any): boolean => typeof ref === 'string')
        .map((ref: string): string => ref.split('/').pop() ?? ref)
        .filter((id: string): boolean => id !== '');
}

/**
 * One recipient's result: how it scored, what was said, and the admin's reply into it.
 *
 * Modelled on the portal's result screen, which reads top to bottom as **who, how it scored, what
 * was said**. That order is the point: an admin opening a result wants the verdict before the
 * transcript, and the old HQ modal led with three bordered boxes, two of which said "Random Box".
 *
 * **The reply goes out in the voice the original did**, which is why there are no sender controls.
 * HQ-API reads the mode and persona the *result* recorded at send time rather than the run's
 * current ones, and threads onto the last inbound message, so a reply cannot arrive from somebody
 * the recipient has never heard from.
 */
export const AdminMiniResultModal: (props: AdminMiniResultModalProps) => JSX.Element =
    (props: AdminMiniResultModalProps): JSX.Element => {
        const notify = useNotify();
        const refresh = useRefresh();
        const extensions = useExtensions();
        const editorRef: RefObject<RichTextEditorRef | null> = useRef<RichTextEditorRef>(null);
        const { sending, sendEmail } = useAdminMiniSendEmail(props.tool);

        const [subject, setSubject] = useState<string>('');
        const [report, setReport] = useState<AdminMiniSendReport | undefined>(undefined);

        const result: any = props.result;
        const refs: any[] = messageRefsOf(result);
        const missing: string[] = idsNeeding(refs);

        // Only asks when there is something to ask for. `enabled` matters: an empty `ids` array
        // still issues a request on some providers, and this modal opens on every row click.
        const { data: fetched } = useGetMany(
            'email_messages',
            { ids: missing },
            { enabled: missing.length > 0 }
        );

        const messages: any[] = missing.length > 0
            ? (fetched ?? [])
            : refs.filter((ref: any): boolean => typeof ref === 'object' && ref !== null);
        const recipient: string = result?.adminTarget?.name
            ?? result?.adminTarget?.email
            ?? `Result ${ result?.id ?? '' }`;

        const canReply: boolean = messages.some((m: any): boolean => m?.direction === 'inbound');

        async function handleSend(): Promise<void> {
            const body: string = editorRef.current?.editor?.getHTML() ?? '';
            // Each tool names the result differently in its own send payload, so both keys go.
            const answer: AdminMiniSendReport = await sendEmail({
                mysteryShopperResultId: result?.id,
                complianceSnifferResultId: result?.id,
                subject: subject,
                body: body,
            });

            setReport(answer);
            notify(answer.message, { type: answer.status === 'success' ? 'info' : 'warning' });

            if (answer.status === 'success') {
                editorRef.current?.editor?.commands.clearContent();
                setSubject('');
                refresh();
            }
        }

        return (
            <AdminMiniDialog open={ props.open }
                             onClose={ props.onClose }
                             width="md"
                             title={ recipient }
                             subtitle={ `${ props.tool.title } result` }>
                <Stack spacing={ 2.5 }>
                    <Box sx={ { display: 'flex', gap: '8px', flexWrap: 'wrap' } }>
                        <Chip size="small" variant="outlined" label={ result?.status ?? 'unknown' }
                              sx={ { textTransform: 'capitalize' } } />
                        { result?.adminTarget?.email !== undefined && (
                            <Chip size="small" variant="outlined" label={ result.adminTarget.email } />
                        ) }
                        { result?.senderMode !== undefined && result?.senderMode !== null && (
                            <Chip size="small" variant="outlined"
                                  label={ result.senderMode === 'nok'
                                      ? 'Sent as a Nok persona'
                                      : 'Sent from a person here' } />
                        ) }
                        { result?.creditsCharged !== undefined && result?.creditsCharged !== null && (
                            <Chip size="small" variant="outlined"
                                  label={ `${ result.creditsCharged } credits` } />
                        ) }
                    </Box>

                    <AdminMiniScoreCard systemCode={ props.tool.systemCode }
                                        runId={ props.runId }
                                        resultId={ result?.id } />

                    <Divider />

                    <Box>
                        <Typography sx={ {
                            fontSize: 'var(--wf-eyebrow)',
                            letterSpacing: 'var(--wf-track)',
                            textTransform: 'uppercase',
                            color: 'text.secondary',
                            fontWeight: 700,
                            marginBottom: '10px',
                        } }>
                            { `Conversation (${ messages.length })` }
                        </Typography>

                        { messages.length === 0 && (
                            <Typography sx={ { fontSize: '13px', color: 'text.secondary' } }>
                                { 'Nothing has been sent or received on this one yet.' }
                            </Typography>
                        ) }

                        <Stack spacing={ 1.5 }>
                            { messages.map((message: any): JSX.Element => {
                                const inbound: boolean = message?.direction === 'inbound';

                                return (
                                    <Box key={ message?.id }
                                         sx={ {
                                             border: '1px solid',
                                             borderColor: 'divider',
                                             borderLeft: '3px solid',
                                             // Inbound is the half worth finding at a glance: it is
                                             // what the run exists to collect.
                                             borderLeftColor: inbound ? 'primary.main' : 'divider',
                                             borderRadius: '8px',
                                             padding: '12px',
                                         } }>
                                        <Box sx={ {
                                            display: 'flex',
                                            justifyContent: 'space-between',
                                            gap: '12px',
                                            marginBottom: '6px',
                                        } }>
                                            <Typography sx={ { fontSize: '13px', fontWeight: 600 } }>
                                                { message?.subject ?? '(no subject)' }
                                            </Typography>
                                            <Typography sx={ {
                                                fontSize: '11px',
                                                color: 'text.secondary',
                                                whiteSpace: 'nowrap',
                                            } }>
                                                { inbound ? 'Received' : 'Sent' }
                                            </Typography>
                                        </Box>
                                        { /* The email in its own document, so its styles apply and
                                             stay inside it. The plain body is the fallback. */ }
                                        <AdminMiniEmailFrame html={ message?.bodyHtml }
                                                             text={ message?.body } />
                                    </Box>
                                );
                            }) }
                        </Stack>
                    </Box>

                    { props.access.can('send') && (
                        <>
                            <Divider />

                            { report !== undefined && (
                                <Alert severity={ report.status === 'success' ? 'success' : 'error' }
                                       sx={ { fontSize: '13px' } }>
                                    { report.message }
                                </Alert>
                            ) }

                            { !canReply && (
                                <Alert severity="info" sx={ { fontSize: '13px' } }>
                                    { 'You can reply once they have written back. A reply threads onto their last message.' }
                                </Alert>
                            ) }

                            { canReply && (
                                <Stack spacing={ 1.5 }>
                                    <TextField size="small"
                                               fullWidth
                                               label="Subject"
                                               placeholder="Leave empty to keep theirs"
                                               value={ subject }
                                               disabled={ sending }
                                               onChange={ (e: ChangeEvent<HTMLInputElement>): void =>
                                                   setSubject(e.target.value) } />

                                    <Box className="cms-editor" sx={ {
                                        border: '1px solid',
                                        borderColor: 'divider',
                                        borderRadius: '10px',
                                        overflow: 'hidden',
                                        '& .ms_editor__container': { minHeight: '160px' },
                                    } }>
                                        <RichTextEditor ref={ editorRef }
                                                        extensions={ extensions }
                                                        content=""
                                                        editable={ !sending }
                                                        editorProps={ { attributes: { class: 'ms_editor__container' } } }
                                                        renderControls={ (): JSX.Element => <EditorControls /> } />
                                    </Box>

                                    <Box sx={ { display: 'flex', justifyContent: 'flex-end' } }>
                                        <Button variant="contained"
                                                disabled={ sending }
                                                onClick={ (): void => { void handleSend(); } }
                                                startIcon={ sending
                                                    ? <CircularProgress size={ 16 } color="inherit" />
                                                    : <SendIcon /> }>
                                            { sending ? 'Sending' : 'Send reply' }
                                        </Button>
                                    </Box>
                                </Stack>
                            ) }
                        </>
                    ) }
                </Stack>
            </AdminMiniDialog>
        );
    };
